import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Rss, Plus, Loader2, Trash2, Power, Search,
  ExternalLink, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const HEALTH_COLOURS = {
  healthy:  'bg-emerald-500',
  warning:  'bg-amber-500',
  degraded: 'bg-orange-500',
  failed:   'bg-red-500',
  blocked:  'bg-rose-600',
  unknown:  'bg-slate-300',
};

const PUBLISHING_MODE_LABELS = {
  auto_publish:  { label: 'Auto-publish',  colour: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  review_queue:  { label: 'Review queue',  colour: 'bg-amber-50 text-amber-700 border-amber-200' },
  // legacy, kept so any old row still renders a label
  auto_draft:    { label: 'Review queue',  colour: 'bg-amber-50 text-amber-700 border-amber-200' },
};


function SubscriptionRow({ sub, onToggle, onDelete, onModeChange }) {
  const [editingMode, setEditingMode] = useState(false);
  const mode = PUBLISHING_MODE_LABELS[sub.publishing_mode] || PUBLISHING_MODE_LABELS.review_queue;
  const healthColour = HEALTH_COLOURS[sub.health_status] || HEALTH_COLOURS.unknown;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors shadow-sm">
      <div className="flex items-start gap-3">
        <span className={`w-2 h-2 rounded-full ${healthColour} mt-2 shrink-0`} title={sub.health_status} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Link to={`/feeds/sources/${sub.feed_source_id}`}
              className="text-slate-900 font-semibold hover:text-blue-600 transition-colors">
              {sub.source_name}
            </Link>
            {!editingMode ? (
              <button onClick={() => setEditingMode(true)}
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${mode.colour} hover:opacity-80 transition-opacity`}>
                {mode.label}
              </button>
            ) : (
              <select value={sub.publishing_mode} autoFocus
                onChange={e => { onModeChange(sub, e.target.value); setEditingMode(false); }}
                onBlur={() => setEditingMode(false)}
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-slate-300 text-slate-700">
                <option value="review_queue">Review queue</option>
                <option value="auto_publish">Auto-publish</option>
              </select>
            )}
            {!sub.is_active && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">
                Paused
              </span>
            )}
          </div>

          <p className="text-xs text-slate-400 truncate font-mono">{sub.source_url}</p>

          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
            {sub.primary_organisation_name && <span>Org: {sub.primary_organisation_name}</span>}
            <span>Parser: {sub.parser_type}</span>
            {sub.last_fetched_at && (
              <span>Last fetched: {new Date(sub.last_fetched_at).toLocaleString()}</span>
            )}
            {sub.max_stories_per_day && <span>Limit: {sub.max_stories_per_day}/day</span>}
          </div>

          {sub.last_error_message && sub.health_status !== 'healthy' && (
            <p className="text-xs text-red-500 mt-2 italic">{sub.last_error_message}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onToggle(sub)}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
            title={sub.is_active ? 'Pause' : 'Resume'}>
            <Power size={14} />
          </button>
          <button onClick={() => onDelete(sub)}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Remove subscription">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}


function AddSubscriptionPanel({ pubId, subscribedSourceIds, onAdded, onClose }) {
  const [sources, setSources]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [adding, setAdding]     = useState(null);
  const [mode, setMode]         = useState('review_queue');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/feeds/sources', { params: { is_active: true } });
        setSources(data || []);
      } catch (err) {
        toast.error('Failed to load sources');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const available = useMemo(() => {
    let list = sources.filter(s => !subscribedSourceIds.includes(s.id));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.source_name?.toLowerCase().includes(q) ||
        s.source_url?.toLowerCase().includes(q) ||
        s.primary_organisation_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [sources, subscribedSourceIds, search]);

  const handleAdd = async (src) => {
    setAdding(src.id);
    try {
      await api.post(`/feeds/publications/${pubId}/subscriptions`, {
        feed_source_id: src.id,
        publishing_mode: mode,
      });
      toast.success(`Subscribed to ${src.source_name}`);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Subscribe to a source</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xs">Cancel</button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search sources..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500" />
        </div>
        <select value={mode} onChange={e => setMode(e.target.value)}
          className="px-2 py-1.5 bg-white border border-slate-200 rounded text-xs text-slate-700 focus:outline-none focus:border-blue-500">
          <option value="review_queue">Review queue</option>
          <option value="auto_publish">Auto-publish</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
          <Loader2 size={14} className="animate-spin" /> Loading sources...
        </div>
      ) : available.length === 0 ? (
        <p className="text-slate-400 text-sm py-4 text-center">
          {sources.length === subscribedSourceIds.length
            ? 'All available sources are already subscribed.'
            : 'No sources match your search.'}
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-1">
          {available.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 rounded">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 truncate">{s.source_name}</p>
                <p className="text-[11px] text-slate-400 truncate">
                  {s.primary_organisation_name && `${s.primary_organisation_name} • `}{s.parser_type}
                </p>
              </div>
              <button onClick={() => handleAdd(s)} disabled={adding === s.id}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2.5 py-1 rounded text-xs font-medium shrink-0">
                {adding === s.id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                Subscribe
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PublicationSubscriptionsTab({ pubId, onChange }) {
  const [subs, setSubs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { load(); }, [pubId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feeds/publications/${pubId}/subscriptions`);
      setSubs(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (sub) => {
    try {
      await api.put(`/feeds/subscriptions/${sub.id}`, { is_active: !sub.is_active });
      toast.success(sub.is_active ? 'Paused' : 'Resumed');
      load(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Remove "${sub.source_name}" from this publication?`)) return;
    try {
      await api.delete(`/feeds/subscriptions/${sub.id}`);
      toast.success('Subscription removed');
      load(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleModeChange = async (sub, newMode) => {
    try {
      await api.put(`/feeds/subscriptions/${sub.id}`, { publishing_mode: newMode });
      toast.success('Mode updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const subscribedSourceIds = subs.map(s => s.feed_source_id);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-400">
          {subs.length} {subs.length === 1 ? 'source subscribed' : 'sources subscribed'}
        </p>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">
            <Plus size={13} /> Add subscription
          </button>
        )}
      </div>

      {showAdd && (
        <AddSubscriptionPanel
          pubId={pubId}
          subscribedSourceIds={subscribedSourceIds}
          onAdded={() => { setShowAdd(false); load(); onChange?.(); }}
          onClose={() => setShowAdd(false)} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading...
        </div>
      ) : subs.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Rss size={28} className="text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 text-sm font-medium mb-1">No subscriptions yet</p>
          <p className="text-slate-400 text-xs mb-4">Subscribe to a source to start ingesting stories.</p>
          {!showAdd && (
            <button onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">
              <Plus size={13} /> Add subscription
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {subs.map(s => (
            <SubscriptionRow key={s.id} sub={s}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onModeChange={handleModeChange} />
          ))}
        </div>
      )}
    </div>
  );
}